import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideCheck } from '@ng-icons/lucide';
import { BrnDialogRef, BrnDialogTitle, injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import { DialogSheetComponent, dialogSheetHostClass } from '@lite-workspace/shared/helm-ui/dialog';

export interface RetouchDoneDialogContext {
  count: number;
  thumbs: readonly string[];
  /** `null` = the photographer stands in the run's own project: nothing to open. */
  projectName: string | null;
}

export type RetouchDoneResult = 'open' | undefined;

/** The end of a retouch run the photographer walked away from: what landed, and the way back to it. */
@Component({
  selector: 'app-retouch-done-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BrnDialogTitle, DialogSheetComponent, HlmButton, NgIcon],
  viewProviders: [provideIcons({ lucideCheck })],
  host: { '[class]': 'hostClass' },
  template: `
    <hlm-dialog-sheet>
      <ng-template>
        <div class="flex items-center gap-3">
          <span class="grid size-10 shrink-0 place-items-center rounded-[var(--r-s)] bg-ok-soft text-ok">
            <ng-icon name="lucideCheck" size="20" />
          </span>
          <h2 brnDialogTitle class="text-lg leading-snug font-bold text-ink">{{ title }}</h2>
        </div>

        @if (ctx.thumbs.length) {
          <div class="mt-5 flex gap-2 overflow-hidden">
            @for (src of ctx.thumbs; track $index) {
              <img [src]="src" alt="" class="h-19 w-auto max-w-28.5 shrink-0 rounded-[var(--r-s)] bg-bg object-cover" />
            }
          </div>
        }

        <p class="mt-5 text-base text-ink">Отретушированные фото автоматически размещены в альбомах</p>

        <div class="mt-6 flex gap-3">
          @if (ctx.projectName; as name) {
            <button hlmBtn type="button" variant="outline" class="h-12 flex-1 justify-center" (click)="close()">Понятно</button>
            <button hlmBtn type="button" class="h-12 flex-1 justify-center" (click)="open()">Открыть «{{ name }}»</button>
          } @else {
            <button hlmBtn type="button" class="h-12 flex-1 justify-center" (click)="close()">Понятно</button>
          }
        </div>
      </ng-template>
    </hlm-dialog-sheet>
  `,
})
export class RetouchDoneDialogComponent {
  protected readonly hostClass = dialogSheetHostClass('l');
  private readonly dialogRef = inject(BrnDialogRef);
  protected readonly ctx = injectBrnDialogContext<RetouchDoneDialogContext>();

  protected readonly title = this.ctx.projectName
    ? `Отретушировали ${this.ctx.count} фото в проекте «${this.ctx.projectName}»`
    : `Отретушировали ${this.ctx.count} фото`;

  protected close(): void {
    this.dialogRef.close(undefined);
  }

  protected open(): void {
    this.dialogRef.close('open');
  }
}
